import type { ApiContext } from "@hobbyshopify/backend-client";
import { BaseController, Rx, type SubtreeModel } from "subtree.js/core";
import type { AuthHandler } from "trunk.js";
import type { AppDeps } from "../../app-deps";
import type { BackendApi } from "../../core/ops/api";
import { failedLoad, idleLoad, type LoadStatus, loaded, loading } from "../../core/primitives/load-status";

export class CustomerQrState {
  /** The entry id (shop or `loc_` counter) the dialog is open for, or null when closed. */
  readonly storeId = new Rx<string | null>(null);
  readonly status = new Rx<LoadStatus>(idleLoad);
  /** The customer entry URL: what the QR encodes and what the merchant can paste. */
  readonly url = new Rx<string | null>(null);
}

export abstract class CustomerQrActions {
  abstract open(storeId: string): void;
  abstract close(): void;
}

export type CustomerQrDeps = Pick<AppDeps, "client" | "authHandler">;

/** The shop's customer entry link, for the "Show QR code" button. */
export function loadCustomerLink(o: { client: BackendApi; authHandler: AuthHandler<ApiContext>; storeId: string }) {
  return o.authHandler.callApi((ctx) => o.client.stores.customerLink(ctx, o.storeId));
}

/**
 * The customer QR dialog: a section a page owns for its lifetime, opened per
 * shop. The view renders the code from `url`; the fetch is the only logic here.
 */
export class CustomerQrController extends BaseController implements CustomerQrActions {
  readonly state = new CustomerQrState();

  constructor(
    subtree: SubtreeModel,
    readonly deps: CustomerQrDeps,
  ) {
    super();
    subtree.put(CustomerQrState, this.state);
    subtree.put(CustomerQrActions, this);
  }

  open = (storeId: string) => {
    this.state.storeId.value = storeId;
    this.state.url.value = null;
    this.state.status.value = loading;
    void this.#load(storeId);
  };

  close = () => {
    this.state.storeId.value = null;
    this.state.url.value = null;
    this.state.status.value = idleLoad;
  };

  async #load(storeId: string): Promise<void> {
    const r = await loadCustomerLink({ ...this.deps, storeId });
    if (this.isDisposed || this.state.storeId.value !== storeId) return;
    if (!r.ok) {
      this.state.status.value = failedLoad(r.errors);
      return;
    }
    this.state.url.value = r.value;
    this.state.status.value = loaded;
  }
}
